import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, ShoppingCart, Globe, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../contexts/CartContext';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "./ui/sheet";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";

export default function Header() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { getCartCount } = useCart();
  const cartCount = getCartCount();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'ko' : 'en';
    i18n.changeLanguage(newLang);
  };

  const tigerCircleItems = [
    { label: 'Know Your Body Type', path: '/tiger-circle/body-type' },
    { label: 'Tea Ritual', path: '/tiger-circle/tea' },
  ];

  return (
    <header className="sticky top-0 z-50 glass border-b border-dark-cedar/10 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2" data-testid="header-logo">
          <div className="font-playfair text-2xl font-bold text-dark-cedar tracking-tight">
            Tiger Wellness Club
          </div>
        </Link>

        {/* Desktop Navigation */}
        <NavigationMenu className="hidden md:flex">
          <NavigationMenuList className="flex items-center space-x-6">
            <NavigationMenuItem>
              <Link to="/" className="text-dark-cedar font-inter text-sm font-medium transition-colors hover:text-tiger-orange">
                {t('nav.home')}
              </Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <NavigationMenuTrigger className="text-dark-cedar font-inter text-sm font-medium">
                Tiger Circle
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid w-[400px] gap-3 p-4">
                  {tigerCircleItems.map((item) => (
                    <li key={item.path}>
                      <NavigationMenuLink asChild>
                        <Link
                          to={item.path}
                          className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-desert-sand/30 hover:text-tiger-orange focus:bg-desert-sand/30"
                        >
                          <div className="text-sm font-medium leading-none">{item.label}</div>
                        </Link>
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <Link to="/shop" className="text-dark-cedar font-inter text-sm font-medium transition-colors hover:text-tiger-orange">
                {t('nav.shop')}
              </Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <Link to="/about" className="text-dark-cedar font-inter text-sm font-medium transition-colors hover:text-tiger-orange">
                {t('nav.about')}
              </Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <Link to="/journal" className="text-dark-cedar font-inter text-sm font-medium transition-colors hover:text-tiger-orange">
                {t('nav.journal')}
              </Link>
            </NavigationMenuItem>
            
            <NavigationMenuItem>
              <Link to="/account" className="text-dark-cedar font-inter text-sm font-medium transition-colors hover:text-tiger-orange">
                {t('nav.account')}
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Actions */}
        <div className="hidden md:flex items-center space-x-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            className="text-dark-cedar"
            data-testid="language-toggle"
          >
            <Globe className="w-4 h-4 mr-2" />
            {i18n.language.toUpperCase()}
          </Button>
          <button
            onClick={() => navigate('/cart')}
            className="relative p-2 text-dark-cedar hover:text-tiger-orange transition-colors"
            data-testid="cart-button"
          >
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-tiger-orange text-soft-ivory text-xs rounded-full flex items-center justify-center font-semibold">
                {cartCount}
              </span>
            )}
          </button>
          <Button
            onClick={() => navigate('/tiger-circle/tea')}
            className="bg-tiger-orange hover:bg-tiger-orange/90 text-soft-ivory rounded-full px-6"
            data-testid="start-ritual-button"
          >
            {t('hero.ctaPrimary')}
          </Button>
        </div>

        {/* Mobile Menu */}
        <div className="md:hidden flex items-center space-x-4">
          <Button variant="ghost" size="sm" onClick={toggleLanguage} className="text-dark-cedar p-2">
            <Globe className="w-5 h-5" />
          </Button>
          <button onClick={() => navigate('/cart')} className="relative p-2 text-dark-cedar">
            <ShoppingCart className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-tiger-orange text-soft-ivory text-xs rounded-full flex items-center justify-center font-semibold">
                {cartCount}
              </span>
            )}
          </button>
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <button className="p-2 text-dark-cedar" data-testid="mobile-menu-toggle">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-soft-ivory w-[300px]">
              <nav className="flex flex-col space-y-6 mt-8">
                <Link to="/" className="text-dark-cedar font-inter text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Home
                </Link>
                <div className="space-y-3">
                  <div className="text-dark-cedar font-inter text-lg font-semibold">Tiger Circle</div>
                  {tigerCircleItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block pl-4 text-dark-cedar/80 font-inter text-base"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
                <Link to="/shop" className="text-dark-cedar font-inter text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Shop
                </Link>
                <Link to="/about" className="text-dark-cedar font-inter text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  About
                </Link>
                <Link to="/journal" className="text-dark-cedar font-inter text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Journal
                </Link>
                <Link to="/account" className="text-dark-cedar font-inter text-lg font-medium" onClick={() => setMobileMenuOpen(false)}>
                  My Account
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}